import { zodResolver } from '@hookform/resolvers/zod';
import { Globe } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { useUpdateStoreURL } from '@/hooks/useUpdateStoreURL';
import i18n from '@/lib/i18n';

const formSchema = z.object({
  url: z
    .string()
    .min(1, {
      message: i18n.t(
        'pages.Settings.components.General.components.StoreForm.validation.min',
      ),
    })
    .url(
      i18n.t(
        'pages.Settings.components.General.components.StoreForm.validation.url',
      ),
    ),
});

type FormData = z.infer<typeof formSchema>;

export function StoreForm() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.General.components.StoreForm',
  });

  const { user } = useAuth();

  const customerUrl = user?.customer.url;

  const [isEditable, setIsEditable] = useState(() => !customerUrl);

  const {
    formState: { isSubmitting, errors },
    register,
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      url: customerUrl ?? '',
    },
  });

  const { updateStoreURL } = useUpdateStoreURL();

  const onSubmit = handleSubmit(async ({ url }) => {
    try {
      await updateStoreURL({
        storeURL: url,
      });

      toast.success(t('toast.success'));
      setIsEditable(false);
    } catch {
      toast.error(t('toast.error'));
    }
  });

  return (
    <div className="space-y-2">
      {(!customerUrl || isEditable) && (
        <form className="space-y-2" onSubmit={onSubmit}>
          <FormGroup
            className="space-y-2"
            error={{ message: errors.url?.message, font: 'sm' }}
          >
            <Label htmlFor="storeURL" className="text-lg font-semibold">
              {t('title')}
            </Label>

            <Input
              id="storeURL"
              placeholder={t('storeUrlPlaceholder')}
              disabled={!isEditable}
              {...register('url')}
            />

            {!errors.url?.message && (
              <p className="text-sm text-muted-foreground">{t('urlError')}</p>
            )}
          </FormGroup>

          <div className="space-x-2">
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t('savingButton') : t('saveButton')}
            </Button>

            {customerUrl && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditable(false)}
              >
                {t('cancelButton')}
              </Button>
            )}
          </div>
        </form>
      )}

      {customerUrl && !isEditable && (
        <div className="flex items-center gap-2">
          <div className="flex items-center space-x-2">
            <Globe className="h-4 w-4 text-muted-foreground" />

            <a
              href={user.customer.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm text-muted-foreground"
            >
              {user?.customer.url}
            </a>
          </div>

          <Button size="sm" onClick={() => setIsEditable(true)}>
            {t('editButton')}
          </Button>
        </div>
      )}
    </div>
  );
}
